/**
 * **One occurrence, in full, in the pane.** KANON `ankiety-klienta-2026-09-16`,
 * `szkice/kierunek-a.html`, as the owner refined it on 16 September 2026.
 *
 * ── Four BANDS, not a numbered list of questions ─────────────────────────────────────────────
 * The first build drew one row per question, which at the card's width left „Tak" the better part
 * of a screen from the question that asked it and gave a paragraph of free text the same shape as a
 * weight. The report is sorted instead into four bands, each an eyebrow over content that suits the
 * kind of answer in it:
 *
 *   ODCZYTY       every quantity, one per ROW, on three left-anchored tracks
 *   PEŁNY POMIAR  the ten girths as tiles, with how complete the set is as a muted aside
 *   ODPOWIEDZI    the questions answered in words, on ODCZYTY's own two tracks
 *   MEDIA         the photographs and the clip, framed in one inset band
 *
 * Which band a question belongs in is a decision about the DOMAIN, so it is `model/surveyReport`
 * and not four filters here. A two-option choice is a reading because its answer is one word out of
 * two; a three-option one is prose.
 *
 * ── The HEAD is not here either ──────────────────────────────────────────────────────────────
 * It is `SurveyPaneHead`, outside the keyed box this component is drawn inside. The compare control
 * went to the stream's own head on 16 September 2026, and the title, the sentence and the one ACT
 * followed the head out on the focus round: a control rebuilt on every press cannot keep focus.
 *
 * ── It is not a dialog any more, and that removed three mechanisms ───────────────────────────
 * It used to be a modal over the matrix, which needed a focus trap, a `finalFocus` resolved out of
 * the DOM at close time because ‹ and › changed the answer, and a right rail of sparklines put
 * there because the dialog COVERED the chart strip. In the pane none of those exist: the strip is
 * directly above and visible, focus never leaves the stream row the trainer pressed, and walking the
 * form is what the stream is.
 *
 * ── It re-reads nothing ──────────────────────────────────────────────────────────────────────
 * `model/surveyHistory` has already built every answer of every occurrence of this form, with the
 * deltas computed against the previous ANSWERED one. Swapping the pane costs an index.
 *
 * „Porównaj z bazowym" is the one thing in the canon that is NOT drawn: there is no baseline concept
 * on this contract — no occurrence is marked as one — and a control that picks the first column in
 * view and calls it the baseline would be inventing the domain.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { RequiredMark } from '~/shared/ui/RequiredMark';

import { useReadingLabels, type SurveyReadingLabels } from '../lib/useReadingLabels';
import { changeWithUnit } from '../model/surveyHistory';
import type { SurveyCellView, SurveyGoalView, SurveyHistoryView } from '../model/surveyHistory';
import { buildSurveyReport, type SurveyReportEntry } from '../model/surveyReport';
import { ChangeArrowIcon } from './SurveyIcons';
import { SurveyReading, TONE_CLASS } from './SurveyReading';
import styles from './SurveyReport.module.css';

export interface SurveyReportProps {
  readonly view: SurveyHistoryView;
}

export function SurveyReport({ view }: SurveyReportProps) {
  const { t } = useTranslation();
  const labels = useReadingLabels();
  const selected = view.selected;
  const report = selected === null ? null : buildSurveyReport(view, selected.id);
  if (report === null) return null;

  return (
    <>
      {report.readings.length === 0 ? null : (
        <Band title={t('web:clientDetail.surveys.bandReadings')}>
          <dl className={styles.readings}>
            {report.readings.map((entry) => (
              <ReadingRow key={entry.key} entry={entry} labels={labels} />
            ))}
          </dl>
        </Band>
      )}

      {report.measurement === null ? null : (
        <Band
          title={t('web:clientDetail.surveys.bandMeasurement')}
          aside={report.measurement.caption}
        >
          <dl className={styles.tiles}>
            {report.measurement.fields.map((entry) => (
              <Tile key={entry.key} entry={entry} labels={labels} />
            ))}
          </dl>
        </Band>
      )}

      {report.answers.length === 0 ? null : (
        <Band title={t('web:clientDetail.surveys.bandAnswers')}>
          <div className={styles.answers}>
            {report.answers.map((entry) => (
              <section className={styles.answer} key={entry.key}>
                <h4 className={styles.answerLabel}>
                  {entry.label}
                  <Required entry={entry} />
                </h4>
                <div className={styles.answerBody}>
                  <div className={styles.answerValue}>
                    {entry.kind === 'goal' && entry.cell.goal != null ? (
                      <GoalTiles goal={entry.cell.goal} />
                    ) : (
                      <SurveyReading
                        cell={entry.cell}
                        kind={entry.kind}
                        showDelta={false}
                        showScaleFigure
                        labels={labels}
                      />
                    )}
                  </div>
                  {/* What the client said LAST time, under what she said this time. A question that
                    * reads as words has no subtraction to offer, and this is the comparison the
                    * trainer is making anyway — the canon's own plate. It stands in the ANSWER's
                    * column rather than across the row: it is a second line of this answer. */}
                  {entry.cell.previousText === null ? null : (
                    <p className={styles.previous}>
                      <span className={styles.previousAt}>
                        {t('web:clientDetail.surveys.previouslyAt', {
                          at: entry.cell.previousAtLabel ?? '',
                        })}
                      </span>
                      <span className={styles.previousText}>{entry.cell.previousText}</span>
                    </p>
                  )}
                </div>
              </section>
            ))}
          </div>
        </Band>
      )}

      {report.media.length === 0 ? null : (
        <Band title={t('web:clientDetail.surveys.bandMedia')}>
          {/* ONE framed band for both, because they are one act by the client — she stood in front
            * of a camera. Floating on the card they read as two loose strips of image. */}
          <div className={styles.mediaBand}>
            {report.media.map((entry) => (
              <div className={styles.mediaBlock} key={entry.key}>
                <p className={styles.mediaHead}>
                  <span className={styles.mediaName}>
                    {entry.label}
                    <Required entry={entry} />
                  </span>
                  {/* How many poses arrived, or when the recording ages out — both answer a question
                    * the picture itself cannot. */}
                  <em className={styles.mediaNote}>
                    {entry.kind === 'photos'
                      ? entry.cell.photos.length === 0
                        ? null
                        : t('web:clientDetail.surveys.poseCount', {
                            count: entry.cell.photos.length,
                          })
                      : (entry.cell.clip?.expiryLabel ?? null)}
                  </em>
                </p>
                <SurveyReading
                  cell={entry.cell}
                  kind={entry.kind}
                  showDelta={false}
                  labels={labels}
                  mediaClassName={
                    entry.kind === 'photos' ? String(styles.panePhotos) : String(styles.paneClip)
                  }
                  playable
                  poseCaptions={entry.kind === 'photos'}
                />
              </div>
            ))}
          </div>
        </Band>
      )}
    </>
  );
}

/**
 * The grey lock beside a question the client could not skip. Owner, 17 September 2026: the mark the
 * editor and the list card draw must also show in the READ of a filled survey.
 *
 * `shared/ui/RequiredMark` — the same 16px `--text-3` glyph, no border, no fill, no accent — so the
 * three surfaces cannot drift. It carries the WORD „Wymagane" off screen beside the glyph, which is
 * what keeps the state from being colour and shape alone.
 *
 * A value of the full measurement never draws one: it is not a question of its own, and the
 * composite that IS asks for all ten at once. `model/surveyHistory` decides that, not this file.
 */
function Required({ entry }: { readonly entry: SurveyReportEntry }) {
  const { t } = useTranslation();
  if (!entry.required) return null;
  return <RequiredMark label={t('web:clientDetail.surveys.required')} />;
}

/** An eyebrow, an optional muted aside beside it, and the band's content under it. */
function Band({
  title,
  aside,
  children,
}: {
  readonly title: string;
  readonly aside?: string | null;
  readonly children: ReactNode;
}) {
  return (
    <section className={styles.band}>
      <h3 className={styles.bandHead}>
        <span className={styles.bandTitle}>{title}</span>
        {aside === null || aside === undefined ? null : (
          <span className={styles.bandAside}>{aside}</span>
        )}
      </h3>
      {children}
    </section>
  );
}

/**
 * One reading, as a ROW: the question, its value and what it is compared against, on three tracks
 * anchored at the card's left edge. Owner, 16 September 2026.
 *
 * The third track carries exactly one of two things, and which one is the CELL's answer rather than
 * a flag this component is handed: a row with arithmetic behind it has a `deltaText` and no
 * `previousText`, a row whose value is a word has the reverse. „Tak" cannot be subtracted from
 * „Nie", so what a two-option row is compared against is the previous ANSWER — the same comparison
 * the trainer is making, in the same column the weight's „↓ −0,6 kg" stands in.
 */
function ReadingRow({
  entry,
  labels,
}: {
  readonly entry: SurveyReportEntry;
  readonly labels: SurveyReadingLabels;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.readingRow}>
      <dt className={styles.readingName}>
        {entry.label}
        <Required entry={entry} />
      </dt>
      <dd className={styles.readingValue}>
        <SurveyReading
          cell={entry.cell}
          kind={entry.kind}
          showDelta={false}
          showScaleFigure
          labels={labels}
        />
      </dd>
      <dd className={styles.readingChange}>
        {entry.cell.deltaText === null ? (
          entry.cell.previousText === null ? null : (
            <span className={styles.readingPrevious}>
              {t('web:clientDetail.surveys.previouslyWas', {
                at: entry.cell.previousAtLabel ?? '',
                value: entry.cell.previousText,
              })}
            </span>
          )
        ) : (
          <ChangeLine cell={entry.cell} />
        )}
      </dd>
    </div>
  );
}

/**
 * One value of the composite measurement. Ten girths are read as a BLOCK rather than one at a time,
 * which is the one band on this report a grid is right for — and why the readings above it stopped
 * wearing this shape on 16 September 2026.
 */
function Tile({
  entry,
  labels,
}: {
  readonly entry: SurveyReportEntry;
  readonly labels: SurveyReadingLabels;
}) {
  return (
    <div className={styles.tile}>
      <dt className={styles.tileName}>
        {entry.label}
        <Required entry={entry} />
      </dt>
      <dd className={styles.tileValue}>
        <SurveyReading
          cell={entry.cell}
          kind={entry.kind}
          showDelta={false}
          showScaleFigure
          labels={labels}
        />
        <ChangeLine cell={entry.cell} />
      </dd>
    </div>
  );
}

/**
 * The client's goal, as tiles. The owner asked for the goal's type, its figures and its deadline to
 * read „graficznie" rather than as a sentence; the full measurement's tiles are this report's own
 * shape for a handful of facts read as one block, so the goal wears them rather than a new drawing.
 * Its description — the client's own words — is the last tile, across the whole grid, so it reads as
 * part of the goal rather than a loose line under it; it is prose, so it wraps at any length.
 */
function GoalTiles({ goal }: { readonly goal: SurveyGoalView }) {
  const { t } = useTranslation();
  return (
    <dl className={styles.tiles}>
      {goal.tiles.map((tile) => (
        <div className={styles.tile} key={tile.key}>
          <dt className={styles.tileName}>{tile.label}</dt>
          <dd className={styles.tileValue}>
            <span className={styles.goalValue}>
              {tile.value}
              {tile.unit === null ? null : <span className={styles.goalUnit}>{tile.unit}</span>}
            </span>
          </dd>
        </div>
      ))}
      {goal.description === null ? null : (
        <div className={`${String(styles.tile)} ${String(styles.goalWide)}`}>
          <dt className={styles.tileName}>{t('clients.goalBuilder.fields.description')}</dt>
          <dd className={styles.goalDescription}>{goal.description}</dd>
        </div>
      )}
    </dl>
  );
}

/**
 * „↓ −0,6 kg" — the change against the previous ANSWERED occurrence.
 *
 * The arrow, the sign, the unit and the verdict all come off the one cell the model built, so the
 * direction the glyph points and the colour it wears cannot disagree with the number beside them.
 */
function ChangeLine({ cell }: { readonly cell: SurveyCellView }) {
  const { t } = useTranslation();
  if (cell.deltaText === null) return null;
  return (
    <em className={`${String(styles.versus)} ${TONE_CLASS[cell.deltaTone ?? 'flat']}`}>
      <ChangeArrowIcon sign={Math.sign(cell.delta ?? 0)} />
      {changeWithUnit(t, cell.delta ?? 0, cell.deltaText, cell.unit)}
    </em>
  );
}
